import { useTranslation } from '@input-web/configs/locale/locale.config';
import mainRoute from '@input-web/modules/main/views/main/main.route';
import standardListRoute from '@input-web/modules/standard/views/list/standard-list.route';
import { useUserStore } from '@input-web/modules/user/stores/user.store';
import loginRoute from '@input-web/modules/user/views/login/login.route';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { UserRole } from '@user/constants/user.constant';
import { useLayoutEffect, useRef } from 'react';
import useIsMsalProcessing from './use-is-msal-processing.hook';

/**
 * Hook for handling authentication state and routing
 * Manages user authentication flow, tab management, and navigation
 *
 * @param onUnauthorizedLogout - Optional callback to handle logout
 */
export default function useAuthHandler(onUnauthorizedLogout?: VoidFunction) {
  // #region Dependencies
  const { t } = useTranslation();
  const { hydrated, token, user } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isMsalProcessing = useIsMsalProcessing();
  // #endregion

  // #region Effects
  const onUnauthorizedLogoutRef = useRef(onUnauthorizedLogout);
  onUnauthorizedLogoutRef.current = onUnauthorizedLogout;
  useLayoutEffect(() => {
    // Wait for store hydration
    if (!hydrated) return;

    const isLoginRoute = location.pathname === loginRoute.path;
    const isPublicRoute = location.pathname === mainRoute.path || isLoginRoute;
    if (isMsalProcessing && isPublicRoute) return;

    // Redirect to login if no token
    if (!token) {
      if (!isLoginRoute) navigate({ to: loginRoute.fullPath });
      return;
    }

    if (user && user.role !== UserRole.InputAdmin) {
      onUnauthorizedLogoutRef.current?.();
      return;
    }

    // Redirect to standard list from root or login
    if (isPublicRoute) navigate({ to: standardListRoute.fullPath });
  }, [hydrated, token, location.pathname, navigate, user, t, isMsalProcessing]);
  // #endregion
}
